<template>
    <b-card
        :title="translations.addDiscount[lang]"
        :loading="loading"
        :error-message="errorMessage"
        :disabled="!formValid"
        @cancel="$emit('cancel')"
        @submit="create"
        :submit-text="{ el: 'Προσθήκη', en: 'Save', it: 'Aggiungi' }"
        :cancel-text="{ el: 'Κλείσιμο', en: 'Cancel', it: 'Chiudi' }"
    >
        <b-select
            v-model="couponWithDiscount.gift_category_id"
            :items="giftCategories"
            :item-text="`name[${lang}]`"
            item-value="gift_category_id"
            :label="translations.giftCategory[lang]"
            no-top-margin
            :success="success.giftCategory"
            :error-messages="error.giftCategory"
            @focus="error.giftCategory = ''"
            @blur="validateGiftCategory"
        ></b-select>

        <v-row no-gutters align="center">
            <v-col cols="8" class="pr-2">
                <v-menu
                    v-model="menu"
                    max-height="300"
                    offset-y
                    :close-on-content-click="false"
                    nudge-bottom="3"
                    @blur="validateProduct"
                >
                    <template v-slot:activator="{ on }">
                        <v-text-field
                            v-model="search"
                            ref="search"
                            :label="translations.product[lang]"
                            color="secondary"
                            outlined
                            dense
                            clearable
                            v-on="on"
                            hide-details="auto"
                            autocomplete="off"
                            :append-icon="
                                menu ? icons.mdiMenuUp : icons.mdiMenuDown
                            "
                            :loading="productsLoading"
                            :success="success.product"
                            :error-messages="error.product"
                            @focus="error.product = ''"
                            @click:append="
                                () => {
                                    menu = !menu;
                                    $refs.search.focus();
                                }
                            "
                        ></v-text-field>
                    </template>

                    <v-list dense>
                        <template v-if="products.length">
                            <v-list-item
                                v-for="product in products"
                                :key="product.product_id"
                                class="pl-3"
                                @click="
                                    () => {
                                        couponWithDiscount.product_discount_id =
                                            product.product_id;
                                        search = product.name[lang];
                                        menu = false;
                                    }
                                "
                            >
                                <v-list-item-title>
                                    <v-row no-gutters justify="space-between">
                                        <v-col cols="auto">
                                            {{ product.name[lang] }}
                                        </v-col>
                                    </v-row>
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                        <v-list-item v-else>
                            <v-list-item-title
                                v-text="'No Data'"
                            ></v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-col>

            <v-col cols="4" class="mt-3 pl-2">
                <v-sheet
                    color="rgba(234, 237, 241, 0.57)"
                    outlined
                    class="subtitle-2 pa-3"
                >
                    {{ translations.productPrice[lang] }}
                    {{
                        new Intl.NumberFormat("en-US", {
                            style: "currency",
                            currency: "EUR",
                            minimumFractionDigits: 2,
                        }).format(retailPrice)
                    }}
                </v-sheet>
            </v-col>
        </v-row>

        <v-sheet class="mt-3 pa-3 pt-2" outlined>
            <h4
                class="subtitle-1 font-weight-medium"
                v-text="translations.discountType[lang]"
            ></h4>
            <v-radio-group v-model="type" class="mt-3 pt-0" hide-details="auto">
                <v-row no-gutters>
                    <v-col
                        v-for="type in discountTypes"
                        :key="type.text['en']"
                        cols="auto"
                        class="pr-5"
                    >
                        <v-radio color="secondary" :value="type.value">
                            <template v-slot:label>
                                <h4
                                    class="font-weight-medium text--primary"
                                    v-text="type.text[lang]"
                                ></h4>
                            </template>
                        </v-radio>
                    </v-col>
                </v-row>
            </v-radio-group>
        </v-sheet>

        <b-text-field
            v-if="type === 1"
            v-model="couponWithDiscount.discount_percentage"
            :label="translations.amount[lang]"
            type="number"
            prepend-inner-icon="mdiPercent"
            :success="success.discountPercentage"
            :error-messages="error.discountPercentage"
            @focus="error.discountPercentage = ''"
            @blur="validateDiscountPercentage"
        ></b-text-field>
        <b-text-field
            v-else
            v-model="couponWithDiscount.discount_value"
            :label="translations.amount[lang]"
            type="number"
            prepend-inner-icon="mdiCurrencyEur"
            :success="success.discountValue"
            :error-messages="error.discountValue"
            @focus="error.discountValue = ''"
            @blur="validateDiscountValue"
        ></b-text-field>
    </b-card>
</template>

<script>
import { mapState, mapMutations, mapActions } from "vuex";
import translations from "@/utils/translations/storePanel/coupons/couponsWithDiscount/couponWithDiscountForm";
import validators from "@/utils/validators/storePanel/couponWithDiscount";
import debounce from "lodash/debounce";
import { mdiMenuUp, mdiMenuDown } from "@mdi/js";

export default {
    name: "CouponWithDiscountForm",

    mixins: [translations, validators],

    data() {
        return {
            icons: {
                mdiMenuUp,
                mdiMenuDown,
            },
            type: 1,
            discountTypes: [
                {
                    text: { el: "Ποσοστό", en: "Percentage", it: "Valutare" },
                    value: 1,
                },
                {
                    text: {
                        el: "Ευρώ (επί της τιμής)",
                        en: "Euro (discount on price)",
                        it: "Euro (sul prezzo)",
                    },
                    value: 2,
                },
            ],
            retailPrice: 0,
            menu: false,
            search: "",
        };
    },

    computed: {
        ...mapState([
            "loading",
            "errorMessage",
            "resetSuccess",
            "resetValidation",
        ]),
        ...mapState("storePanel/coupons/couponsWithDiscount", [
            "giftCategories",
            "products",
            "couponWithDiscount",
            "productsLoading",
        ]),

        lang() {
            return this.$route.params.lang;
        },

        innerIcon() {
            let icon = "";

            switch (this.couponWithDiscount.product_discount_id) {
                case 1:
                    icon = "mdiPercent";
                    break;
                case 2:
                    icon = "mdiCurrencyEur";
                    break;
            }

            return icon;
        },
    },

    methods: {
        ...mapMutations(["setResetSuccess", "setResetValidation"]),
        ...mapActions("storePanel/coupons/couponsWithDiscount", [
            "getGiftCategories",
            "getProducts",
            "create",
        ]),

        handleSearch() {
            this.getProducts(`?q=${this.search}`);
        },
    },

    watch: {
        ["couponWithDiscount.product_discount_id"](val) {
            if (val) {
                this.retailPrice = +this.products.find(
                    (p) => p.product_id === val
                ).retail_price;
            } else {
                this.retailPrice = 0;
            }
        },

        type(val) {
            if (val === 1) {
                this.couponWithDiscount.discount_value = null;
                this.error.discountValue = "";
            } else if (val === 2) {
                this.couponWithDiscount.discount_percentage = null;
                this.error.discountPercentage = "";
            }
        },

        search(val) {
            this.menu = true;
            if (val) {
                this.debouncedSearch();
            } else {
                this.getProducts(`?page=1`);
            }
        },
    },

    mounted() {
        this.getGiftCategories();
        this.getProducts(`?page=1`);
    },

    created() {
        this.debouncedSearch = debounce(this.handleSearch, 500);
    },
};
</script>
